import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchScholarshipsByStatus,
  updateScholarship,
} from "../store/slices/scholarshipSlice";
import { useSnackbar } from "notistack";
import {
  Award,
  User,
  Mail,
  Clock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Search,
  ArrowUpRight,
  Filter,
  ChevronRight,
} from "lucide-react";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";

const ShortlistedScholarships = () => {
  const dispatch = useDispatch();
  const { scholarships, loading, error } = useSelector(
    (state) => state.scholarship
  );
  const { enqueueSnackbar } = useSnackbar();
  const { theme, gradient } = useSidebarContext();
  const [amount, setAmount] = useState("");
  const [selectedScholarship, setSelectedScholarship] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: "all",
    amountRequest: "all",
    university: "all",
  });

  useEffect(() => {
    dispatch(fetchScholarshipsByStatus("Shortlisted"));
  }, [dispatch]);

  const handleApprove = (scholarship) => {
    setSelectedScholarship(scholarship);
  };

  const handleConfirmApproval = () => {
    if (selectedScholarship && amount) {
      dispatch(
        updateScholarship({
          id: selectedScholarship._id,
          statusUpdate: { status: "Approved", amount: parseFloat(amount) },
        })
      ).then(() => {
        updateScholarship((prevScholarships) =>
          prevScholarships.filter(
            (scholarship) => scholarship._id !== selectedScholarship._id
          )
        );
        enqueueSnackbar("Scholarship approved successfully!", {
          variant: "success",
          icon: <CheckCircle2 className="w-5 h-5" />,
        });
      });

      setAmount("");
      setSelectedScholarship(null);
    } else {
      enqueueSnackbar("Please enter a valid amount", {
        variant: "error",
        icon: <AlertCircle className="w-5 h-5" />,
      });
    }
  };

  const handleReject = (scholarship) => {
    dispatch(
      updateScholarship({
        id: scholarship._id,
        statusUpdate: { status: "Rejected" },
      })
    );
    enqueueSnackbar("Scholarship application rejected", {
      variant: "info",
      icon: <XCircle className="w-5 h-5" />,
    });
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const applyFilters = (scholarships) => {
    return scholarships.filter((scholarship) => {
      // Filter by date range
      if (filters.dateRange !== "all") {
        const applicationDate = new Date(scholarship.applicationDate);
        const today = new Date();

        if (
          filters.dateRange === "today" &&
          applicationDate.toDateString() !== today.toDateString()
        ) {
          return false;
        }

        if (filters.dateRange === "week") {
          const oneWeekAgo = new Date();
          oneWeekAgo.setDate(today.getDate() - 7);
          if (applicationDate < oneWeekAgo) return false;
        }

        if (filters.dateRange === "month") {
          const oneMonthAgo = new Date();
          oneMonthAgo.setMonth(today.getMonth() - 1);
          if (applicationDate < oneMonthAgo) return false;
        }
      }

      // Filter by amount request
      if (filters.amountRequest !== "all" && scholarship.requestedAmount) {
        const amount = parseFloat(scholarship.requestedAmount);

        if (filters.amountRequest === "low" && amount > 50000) return false;
        if (
          filters.amountRequest === "medium" &&
          (amount <= 50000 || amount > 100000)
        )
          return false;
        if (filters.amountRequest === "high" && amount <= 100000) return false;
      }

      // Filter by university
      if (
        filters.university !== "all" &&
        scholarship.university !== filters.university
      ) {
        return false;
      }

      return true;
    });
  };

  const filteredScholarships = scholarships
    .filter(
      (scholarship) =>
        scholarship.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        scholarship.email.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter((scholarship) => applyFilters([scholarship]).length > 0);

  const CurrencySymbol = () => (
    <span className={`w-8 h-8 ${theme.tailwind.text9} font-medium`}>PKR</span>
  );

  const formatPKR = (value) => {
    if (!value) return "";
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  if (loading)
    return (
      <div
        className={`flex items-center justify-center min-h-screen ${gradient}`}
      >
        <div className="relative">
          <div
            className={`w-20 h-20 border-t-4 border-r-4 ${theme.tailwind.border25} rounded-full animate-spin`}
          ></div>
          <Award
            className={`w-8 h-8 ${theme.tailwind.text9} absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2`}
          />
        </div>
      </div>
    );

  if (error)
    return (
      <div
        className={`flex items-center justify-center min-h-screen ${gradient}`}
      >
        <div className="bg-red-500/10 px-6 py-4 rounded-xl backdrop-blur-lg border border-red-500/20">
          <div className="flex items-center gap-3 text-red-300">
            <AlertCircle className="w-6 h-6" />
            <p className="font-medium">
              Error: {error?.message || "An error occurred"}
            </p>
          </div>
        </div>
      </div>
    );

  return (
    <div className={`${gradient} p-8 rounded-xl shadow-lg`}>
      <div className="max-w-8xl mx-auto">
        {/* Header Section */}
        <div className="mb-10">
          <div className="flex items-center gap-4 mb-6">
            <div className={`p-3 ${theme.tailwind.bg25} rounded-xl`}>
              <Award className={`w-8 h-8 ${theme.tailwind.text9}`} />
            </div>
            <div>
              <h2
                className={`text-3xl font-bold ${theme.tailwind.bgr22} bg-clip-text text-transparent`}
              >
                Shortlisted Scholarships
              </h2>
              <p className={`${theme.tailwind.texting2} mt-1`}>
                Review and manage scholarship applications
              </p>
            </div>
          </div>

          <div className="flex gap-4 mb-8">
            <div className="flex-1 relative">
              <Search
                className={`w-5 h-5 ${theme.tailwind.texting2} absolute left-4 top-1/2 transform -translate-y-1/2`}
              />
              <input
                type="text"
                placeholder="Search applications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-12 pr-4 py-3 ${theme.tailwind.bg2} backdrop-blur-xl rounded-xl border ${theme.tailwind.border2} ${theme.tailwind.text9} ${theme.tailwind.placeHolder4} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} transition-all duration-300`}
              />
            </div>
            <div className="relative">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-3 ${
                  theme.tailwind.bg2
                } backdrop-blur-xl rounded-xl border ${
                  showFilters
                    ? `${theme.tailwind.border30} ${theme.tailwind.bg3}`
                    : `${theme.tailwind.border2}`
                } ${theme.tailwind.text9} ${
                  theme.tailwind.bgHover15
                } transition-all duration-300 flex items-center gap-2`}
              >
                <Filter className="w-5 h-5" />
                Filters
                {Object.values(filters).some((val) => val !== "all") && (
                  <span
                    className={`w-2 h-2 rounded-full ${theme.tailwind.bg40} ml-2`}
                  ></span>
                )}
              </button>

              {showFilters && (
                <div
                  className={`absolute top-full right-0 mt-2 w-72 ${theme.tailwind.bg14} rounded-xl border ${theme.tailwind.border2} shadow-lg z-50 p-4 animate-fade`}
                >
                  <h4 className={`${theme.tailwind.text9} font-medium mb-3`}>
                    Filter Options
                  </h4>

                  <div className="mb-4">
                    <label
                      className={`${theme.tailwind.texting2} text-sm mb-1 block`}
                    >
                      Date Range
                    </label>
                    <select
                      value={filters.dateRange}
                      onChange={(e) =>
                        handleFilterChange("dateRange", e.target.value)
                      }
                      className={`w-full py-2 px-3 ${theme.tailwind.bg16} border ${theme.tailwind.border19} rounded-lg ${theme.tailwind.text9} focus:outline-none focus:ring-1 ${theme.tailwind.focusRing2}`}
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">Past Week</option>
                      <option value="month">Past Month</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label
                      className={`${theme.tailwind.texting2} text-sm mb-1 block`}
                    >
                      Amount Requested
                    </label>
                    <select
                      value={filters.amountRequest}
                      onChange={(e) =>
                        handleFilterChange("amountRequest", e.target.value)
                      }
                      className={`w-full py-2 px-3 ${theme.tailwind.bg16} border ${theme.tailwind.border19} rounded-lg ${theme.tailwind.text9} focus:outline-none focus:ring-1 ${theme.tailwind.focusRing2}`}
                    >
                      <option value="all">Any Amount</option>
                      <option value="low">Low (≤ 50,000 PKR)</option>
                      <option value="medium">
                        Medium (50,000 - 100,000 PKR)
                      </option>
                      <option value="high">High (≥ 100,000 PKR)</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label
                      className={`${theme.tailwind.texting2} text-sm mb-1 block`}
                    >
                      University
                    </label>
                    <select
                      value={filters.university}
                      onChange={(e) =>
                        handleFilterChange("university", e.target.value)
                      }
                      className={`w-full py-2 px-3 ${theme.tailwind.bg16} border ${theme.tailwind.border19} rounded-lg ${theme.tailwind.text9} focus:outline-none focus:ring-1 ${theme.tailwind.focusRing2}`}
                    >
                      <option value="all">All Universities</option>
                      <option value="FAST">FAST National University</option>
                      <option value="NUST">NUST</option>
                      <option value="LUMS">LUMS</option>
                      <option value="UET">UET</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={() => {
                        setFilters({
                          dateRange: "all",
                          amountRequest: "all",
                          university: "all",
                        });
                      }}
                      className={`px-3 py-1.5 text-sm ${theme.tailwind.bg5} ${theme.tailwind.bgHover24} rounded-lg ${theme.tailwind.texting1} transition-colors`}
                    >
                      Reset
                    </button>
                    <button
                      onClick={() => setShowFilters(false)}
                      className={`px-3 py-1.5 text-sm ${theme.tailwind.bg13} ${theme.tailwind.bgHover5} rounded-lg text-white transition-colors`}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid gap-6">
          {filteredScholarships.length > 0 ? (
            filteredScholarships.map((scholarship) => (
              <div
                key={scholarship._id}
                className={`group ${theme.tailwind.bg60} backdrop-blur-xl rounded-xl border ${theme.tailwind.border2} ${theme.tailwind.bgHover1} transition-all duration-300`}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-12 ${theme.tailwind.bg25} rounded-xl flex items-center justify-center`}
                      >
                        <User className={`w-6 h-6 ${theme.tailwind.text9}`} />
                      </div>
                      <div>
                        <h3
                          className={`text-xl font-semibold ${theme.tailwind.text9} flex items-center gap-2`}
                        >
                          {scholarship.name}
                          <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </h3>
                        <div className="flex items-center gap-3 mt-1">
                          <div
                            className={`flex items-center gap-1.5 ${theme.tailwind.texting2}`}
                          >
                            <Mail className="w-4 h-4" />
                            <span className="text-sm">{scholarship.email}</span>
                          </div>
                          <div
                            className={`w-1 h-1 rounded-full ${theme.tailwind.bg16}`}
                          />
                          <div className="flex items-center gap-1.5">
                            <Clock
                              className={`w-4 h-4 ${theme.tailwind.text9}`}
                            />
                            <span
                              className={`text-sm font-medium px-2 py-1 rounded-lg ${theme.tailwind.bg25} ${theme.tailwind.text9}`}
                            >
                              {scholarship.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleApprove(scholarship)}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl ${theme.tailwind.bg42} ${theme.tailwind.text16} ${theme.tailwind.bgHover13} transition-all duration-300 group/button`}
                      >
                        <CheckCircle2 className="w-5 h-5" />
                        <span>Approve</span>
                        <ChevronRight className="w-4 h-4 opacity-0 group-hover/button:opacity-100 transition-all duration-300 -mr-2 group-hover/button:mr-0" />
                      </button>
                      <button
                        onClick={() => handleReject(scholarship)}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-all duration-300"
                      >
                        <XCircle className="w-5 h-5" />
                        <span>Reject</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div
              className={`${theme.tailwind.bg60} backdrop-blur-xl rounded-xl border ${theme.tailwind.border2} p-16`}
            >
              <div className="flex flex-col items-center justify-center text-center">
                <div
                  className={`w-16 h-16 ${theme.tailwind.bg25} rounded-full flex items-center justify-center mb-4`}
                >
                  <Award className={`w-8 h-8 ${theme.tailwind.text9}`} />
                </div>
                <h3
                  className={`text-xl font-semibold ${theme.tailwind.text9} mb-2`}
                >
                  No Shortlisted Scholarships Found
                </h3>
                <p className={`${theme.tailwind.texting2} max-w-md`}>
                  There are currently no shortlisted scholarship applications to
                  review. New applications will appear here once they have been
                  shortlisted.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Approval Modal */}
        {selectedScholarship && (
          <div
            className={`fixed inset-0 bg-slate-900/90 backdrop-blur-lg flex items-center justify-center p-4 animate-fade`}
          >
            <div
              className={`${theme.tailwind.bg8} rounded-2xl shadow-2xl border ${theme.tailwind.border2} p-8 w-full max-w-lg animate-scale`}
            >
              <div className="flex items-center gap-4 mb-8">
                <div className={`p-3 ${theme.tailwind.bg25} rounded-xl`}>
                  <CurrencySymbol />
                </div>
                <div>
                  <h3 className={`text-2xl font-bold ${theme.tailwind.text9}`}>
                    Scholarship Amount
                  </h3>
                  <p className={`${theme.tailwind.texting2} mt-1`}>
                    Enter the approved amount for {selectedScholarship.name}
                  </p>
                </div>
              </div>

              <div className="relative mb-8">
                <div className="absolute inset-y-0 left-4 flex items-center gap-1 pointer-events-none">
                  <CurrencySymbol />
                </div>
                <input
                  type="text"
                  value={formatPKR(amount)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    setAmount(value);
                  }}
                  className={`w-full pl-20 pr-4 py-4 rounded-xl ${theme.tailwind.bg2} border ${theme.tailwind.border2} ${theme.tailwind.text9} ${theme.tailwind.placeHolder4} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} transition-all duration-300 font-medium`}
                  placeholder="Enter amount in PKR"
                />
                <div
                  className={`absolute right-4 top-1/2 transform -translate-y-1/2 ${theme.tailwind.texting2} text-sm`}
                >
                  Pakistani Rupee
                </div>
              </div>

              <div className="flex justify-end items-center gap-3">
                <button
                  onClick={() => setSelectedScholarship(null)}
                  className={`px-6 py-3 rounded-xl ${theme.tailwind.bg3} ${theme.tailwind.texting1} ${theme.tailwind.bgHover31} transition-all duration-300`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmApproval}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl ${theme.tailwind.bgr17} text-white ${theme.tailwind.groupHoverG10} transition-all duration-300`}
                >
                  Confirm Approval
                  <ArrowUpRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShortlistedScholarships;
